import { z } from 'zod';

import { CUSTOM_INSTRUCTIONS_MAX_LENGTH } from './constants';

import { sanitizePromptContent } from '@/lib/security/prompt-sanitization';
import { normalizeRepoSyncPath } from '@/lib/wiki/repo-sync/path';

export const WikiSettingScheduleSchema = z.enum(['daily', 'weekly', 'monthly']);
export const WikiRepoSyncRunStatusSchema = z.enum([
  'pending',
  'no_changes',
  'pr_opened',
  'pr_updated',
  'failed',
  'skipped',
]);

export const WikiRepoSyncLatestRunSchema = z.object({
  id: z.string().guid(),
  status: WikiRepoSyncRunStatusSchema,
  skipReason: z.string().nullable(),
  prNumber: z.number().nullable(),
  wikiVersion: z.number().nullable(),
  branchName: z.string().nullable(),
  commitSha: z.string().nullable(),
  errorClass: z.string().nullable(),
  errorMessage: z.string().nullable(),
  pagesChanged: z.number().nullable(),
  createdAt: z.string().datetime(),
  completedAt: z.string().datetime().nullable(),
});

export const WikiSettingResponseSchema = z.object({
  id: z.string().guid().optional(),
  repositoryId: z.number(),
  installationId: z.number(),
  autoRefreshEnabled: z.boolean(),
  autoRefreshSchedule: WikiSettingScheduleSchema,
  customInstructions: z.string().nullable(),
  repoSyncAvailable: z.boolean(),
  repoSyncEnabled: z.boolean(),
  repoSyncPath: z.string(),
  lastSyncedWikiId: z.string().guid().nullable(),
  lastRepoSyncAt: z.string().datetime().nullable(),
  latestRepoSyncRun: WikiRepoSyncLatestRunSchema.nullable(),
  lastAutoRefreshAt: z.string().datetime().nullable(),
  lastManualRefreshAt: z.string().datetime().nullable(),
  canManualRefreshAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime().optional(),
  updatedAt: z.string().datetime().optional(),
  hasActiveSubscription: z.boolean(),
  hasScanAccess: z.boolean().optional(),
});

export const WikiSettingUpdateSchema = z.object({
  installationId: z.number(),
  repositoryId: z.number(),
  autoRefreshEnabled: z.boolean().optional(),
  autoRefreshSchedule: WikiSettingScheduleSchema.optional(),
  repoSyncEnabled: z.boolean().optional(),
  repoSyncPath: z
    .string()
    .optional()
    .transform((value, ctx) => {
      if (value === undefined) {
        return undefined;
      }

      const normalized = normalizeRepoSyncPath(value);
      if (!normalized.success) {
        ctx.addIssue({ code: 'custom', message: normalized.error });
        return z.NEVER;
      }

      return normalized.path;
    }),
  customInstructions: z
    .string()
    .max(CUSTOM_INSTRUCTIONS_MAX_LENGTH)
    .nullable()
    .optional()
    .transform((val) => {
      if (val === undefined) {
        return undefined;
      }
      return sanitizePromptContent(val, { maxLength: CUSTOM_INSTRUCTIONS_MAX_LENGTH });
    }),
});

export type WikiSettingSchedule = z.infer<typeof WikiSettingScheduleSchema>;
export type WikiSettingResponse = z.infer<typeof WikiSettingResponseSchema>;
// The input side: updateWikiSetting parses it, and in zod 4 a field that is optional and then
// transformed is required in the parsed output.
export type WikiSettingUpdateInput = z.input<typeof WikiSettingUpdateSchema>;
